'use client';

import { BoxCTA } from '@lichtblick/photon/atoms/box-cta/box-cta';
import { Headline } from '@lichtblick/photon/atoms/headline/headline';
import { Text } from '@lichtblick/photon/atoms/text/text';
import { EVehicleS } from '@lichtblick/photon/icons/technology/e-vehicle-s';
import { Stepper } from '@lichtblick/photon/molecules/stepper';
import { Textfield } from '@lichtblick/photon/molecules/textfield';
import { useTracker } from '@lichtblick/tracker';
import { type ChangeEventHandler, type FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { CalculatorHomeChargingType } from '../../types/storyblok';
import { CityInput } from '../OAllInOneCalculator/CityInput';
import {
  carEfficiencyLimit,
  carUsage as carUsageConstants,
  ELECTRICITY_USAGE,
  homeChargingPartLimit,
  mileageLimit,
  usageLimitElectricity,
} from '../OAllInOneCalculator/constants';
import { UsageModal } from '../OAllInOneCalculator/UsageModal';
import { useVpCodes } from '../OAllInOneCalculator/useVpCodes';
import styles from './calculator.module.scss';

const calculateUsage = (mileage: number) =>
  Math.round((carEfficiencyLimit.default * (homeChargingPartLimit.default / 100) * mileage) / 100) * 100;

export const CalculatorHomeCharging: FC<CalculatorHomeChargingType> = () => {
  const [postcode, setPostcode] = useState('');
  const [city, setCity] = useState('');

  const [people, setPeople] = useState(1);
  const [electricityUsage, setElectricityUsage] = useState(String(ELECTRICITY_USAGE[0]));
  const [mileage, setMileage] = useState(mileageLimit.default);
  const [carUsage, setCarUsage] = useState(String(carUsageConstants.default));

  const [electricityUsageModalOpen, setElectricityUsageModalOpen] = useState(false);

  const { computeVpCodes } = useVpCodes();
  const { trackCustomEvent } = useTracker();

  const cityChangeHandler = useCallback((city: string, postcode: string) => {
    setCity(city);
    setPostcode(postcode);
  }, []);

  const electricityChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setElectricityUsage(e.currentTarget.value);
  }, []);

  const carUsageChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setCarUsage(e.currentTarget.value);
  }, []);

  const [vpCodes, setVpCodes] = useState<{ uvp?: string; vp?: string }>({});
  const totalUsage = useMemo(() => +electricityUsage + +carUsage, [electricityUsage, carUsage]);

  useEffect(() => setElectricityUsage(String(ELECTRICITY_USAGE[Math.min(Math.max(people - 1, 0), 9)])), [people]);
  useEffect(() => setCarUsage(String(calculateUsage(mileage))), [mileage]);
  useEffect(() => setVpCodes(computeVpCodes()), [computeVpCodes]);

  return (
    <>
      <form
        action="/checkout/"
        className={styles.form}
        id="home-charging-calculator"
        method="GET"
        onSubmit={(e) => {
          if (Number(electricityUsage) > usageLimitElectricity.max) {
            e.preventDefault();
            setElectricityUsageModalOpen(true);

            return;
          }

          if (e.currentTarget.checkValidity()) {
            trackCustomEvent({
              eventName: 'tariff_calculator_show_tariffs',
              customProps: {
                product: 'ZuHause Plus',
                kilometers_traveled: `${mileage} km/Jahr`,
                zip_code: postcode,
                people_counter: people,
                consumption_calculator: `${electricityUsage} kWh/Jahr;${carUsage} kWh/Jahr`,
              },
            });
          }
        }}
      >
        <Headline className={styles.headline} renderAs="h2" size="s">
          ÖkoStrom* E-Auto
          <EVehicleS />
        </Headline>
        <CityInput onChange={cityChangeHandler} />
        <Stepper defaultValue={1} label="Personenanzahl" max={10} min={1} onChange={setPeople} hideLabelSuffix />
        <Textfield
          inputMode="numeric"
          label="Verbrauch Zuhause"
          min={usageLimitElectricity.min}
          onChange={electricityChangeHandler}
          rightSlot={
            <Text className={styles.unit} size="s">
              kWh/Jahr
            </Text>
          }
          type="number"
          value={electricityUsage}
          hideLabelSuffix
        />
        <Stepper
          defaultValue={mileageLimit.default}
          label="Fahrleistung in km/Jahr"
          min={mileageLimit.min}
          step={mileageLimit.step}
          onChange={setMileage}
          hideLabelSuffix
        />
        <Textfield
          inputMode="numeric"
          label="Verbrauch Auto"
          min={usageLimitElectricity.min}
          onChange={carUsageChangeHandler}
          rightSlot={
            <Text className={styles.unit} size="s">
              kWh/Jahr
            </Text>
          }
          type="number"
          required={!+electricityUsage}
          value={carUsage}
          hideLabelSuffix
        />

        <input name="strom" type="hidden" value={totalUsage} />
        <input name="ort" type="hidden" value={city} />
        <input name="plz" type="hidden" value={postcode} />
        {Boolean(vpCodes.vp) && <input name="vp" type="hidden" value={vpCodes.vp} />}
        {Boolean(vpCodes.uvp) && <input name="uvp" type="hidden" value={vpCodes.uvp} />}

        <BoxCTA as="button" type="submit">
          Tarife berechnen
        </BoxCTA>
      </form>
      <UsageModal isOpen={electricityUsageModalOpen} route="/gewerbestrom" setIsOpen={setElectricityUsageModalOpen} />
    </>
  );
};
